// Pulserende markører: tilstandsmaskinen bak «blink opp i noen sekunder».
//
// Eierkravet (2026-08-17): «når det kommer viktig info så skal de sensorene som
// detekterer noe av interesse blinke opp i noen sekunder og med en advarsel som
// lar operatører velge å rette oppmerksomheten mot denne.»
//
// HVORFOR EGEN FIL: `CopMap` bygger et ekte MapLibre-kart i konstruktøren, så
// alt som ligger inne i den klassen kan i praksis ikke enhetstestes. Reglene her
// — når noe slutter å blinke, at et nytt snapshot ikke restarter fasen, og at
// løkka faktisk stopper — er de tre stedene dette kan gå galt, og de er ren
// aritmetikk. Kartet eier bare `requestAnimationFrame` og `setData`.
import { featureCollection, type GeoFeature } from '@skade/contracts';

/**
 * Hvor lenge en markør pulserer. Eieren ba om «noen sekunder».
 *
 * 6 s = tre pulser à 2 s: nok til å bli sett av en operatør som ser bort et
 * øyeblikk, kort nok til at kartet ikke står og blinker. Etterpå er observasjonen
 * fortsatt i kartet og i varselkøen — blinket er en INVITASJON til oppmerksomhet,
 * ikke måten informasjonen lagres.
 */
export const BLINK_VARIGHET_MS = 6000;

/** Én puls (ett «hjerteslag»). */
export const BLINK_PULS_MS = 2000;

export interface BlinkPunkt {
  id: string;
  lon: number;
  lat: number;
}

export class Blinkstyring {
  private aktive = new Map<string, { lon: number; lat: number; start: number }>();

  constructor(
    private readonly varighetMs = BLINK_VARIGHET_MS,
    private readonly pulsMs = BLINK_PULS_MS,
  ) {}

  /**
   * Start pulsering. **Idempotent per id.**
   *
   * ⚠ EN ID SOM ALT BLINKER RESTARTES IKKE. Hubben sender et fullt snapshot
   * jevnlig, og varselkøen kan levere samme observasjon på nytt. Nullet vi fasen
   * hver gang, ville markøren blinket for ALLTID — og en markør som blinker evig
   * er ikke et varsel, den er bakgrunnsstøy operatøren lærer å se bort fra.
   */
  start(punkter: readonly BlinkPunkt[], naa: number): void {
    for (const p of punkter) {
      if (this.aktive.has(p.id)) continue;
      this.aktive.set(p.id, { lon: p.lon, lat: p.lat, start: naa });
    }
  }

  /** Blinker noe nå? Kartet bruker denne til å avgjøre om løkka skal fortsette. */
  get aktiv(): boolean {
    return this.aktive.size > 0;
  }

  /** Antall punkter som pulserer (til test/diagnose). */
  get antall(): number {
    return this.aktive.size;
  }

  clear(): void {
    this.aktive.clear();
  }

  /**
   * Én animasjonsramme: fjern utløpte punkter og returner GeoJSON for resten.
   *
   * `fase` går 0→1 per puls (sagtann), og kartlaget bruker den til å vokse
   * radiusen og fade kanten. Å regne fasen HER, og ikke i kartlagets uttrykk,
   * gjør at pulsformen kan testes uten en GPU.
   */
  ramme(naa: number): ReturnType<typeof featureCollection> {
    const feats: GeoFeature[] = [];
    for (const [id, b] of [...this.aktive]) {
      const gaatt = naa - b.start;
      // Negativ tid (klokka stilt bakover mellom to rammer) skal ikke gi en
      // markør som blinker til klokka har tatt igjen seg selv.
      if (gaatt < 0 || gaatt > this.varighetMs) {
        this.aktive.delete(id);
        continue;
      }
      feats.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [b.lon, b.lat] },
        properties: { fase: (gaatt % this.pulsMs) / this.pulsMs, id },
      } as unknown as GeoFeature);
    }
    return featureCollection(feats);
  }
}
